'use client';

import { useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import ErrorBox from '@/components/ErrorBox';
import LabelBadge from '@/components/LabelBadge';
import SectionTitle from '@/components/SectionTitle';
import DashboardPersonalNoteUnit from '@/components/features/dashboard/unit/DashboardPersonalNoteUnit';
import FormFieldLabel from '@/components/ui/form/FormFieldLabel';
import FormInput from '@/components/ui/form/FormInput';
import FormInputWithLabel from '@/components/ui/form/FormInputWithLabel';
import FormSectionTitle from '@/components/ui/form/FormSectionTitle';

interface DashboardPersonalEmailAddressAuthProps {
    editEmail: string | null;
}
function DashboardPersonalEmailAddressAuth({ editEmail }: DashboardPersonalEmailAddressAuthProps) {
    const { handleError, errorMessage, clearError } = useHandleError();
    const showToastAsync = useToastStore((s) => s.showToastAsync);
    const closeModal = useModalStore((state) => state.closeModal);
    const setUserRegistrationStatus = useAuthStore((s) => s.setUserRegistrationStatus);
    const setUserAuth = useAuthStore((s) => s.setUserAuth);

    const handleAuthCodeInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value.length === 6) {
            try {
                clearError();
                await apiFetch(`/users/me/email`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ code: value, email: editEmail }),
                });
                closeModal();
                setUserRegistrationStatus({ email: 1 });
                const latestUserAuth = useAuthStore.getState().userAuth;
                if (latestUserAuth && editEmail) {
                    setUserAuth({
                        ...latestUserAuth,
                        email: editEmail,
                    });
                }
                await showToastAsync('メールアドレスの設定が完了しました', 'success');
            } catch (err: unknown) {
                handleError(err);
            }
        }
    };

    return (
        <div className="flex flex-col w-full">
            <div className="section-spacer-inner-y"></div>
            <div className="flex justify-start items-center w-full py-4">
                <FormSectionTitle title="認証コード入力" />
            </div>
            <div className="flex flex-col justify-start items-center w-full py-4">
                <div className="flex items-start justify-start w-full">
                    <ErrorBox message={errorMessage?.message} />
                </div>
                <div className="flex flex-col items-center w-full">
                    <div className="flex justify-start items-center w-full">
                        <FormFieldLabel title="新しいメールアドレス" />
                    </div>
                    <div className="my-2"></div>
                    <div className="flex w-full px-4 text-base">{editEmail}</div>
                </div>
                <div className="py-6 md:py-4"></div>
                <div className="flex flex-col justify-center items-center bg-color-white-base w-full h-full">
                    <div className="flex justify-start items-center w-full mb-4">
                        <FormFieldLabel title="認証コード" />
                        <LabelBadge type="required" />
                    </div>
                    <div className="flex flex-col items-start justify-start w-[90%] xl:w-[70%]">
                        <div id="code" className="flex flex-col items-center w-full">
                            <FormInput
                                id="code"
                                name="code"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="＊＊＊＊＊＊"
                                autoComplete="off"
                                autoFocus={true}
                                onChange={handleAuthCodeInputChange}
                                className="rounded-3xl text-center py-4"
                            />
                        </div>
                    </div>
                    <div className="section-spacer-inner-4-y"></div>
                </div>
            </div>
        </div>
    );
}
interface DashboardPersonalEmailAddressEditProps {
    goToAuth: () => void;
    currentEmail: string | null;
    setEditEmail: (editEmail: string) => void;
}
function DashboardPersonalEmailAddressEdit({
    goToAuth,
    currentEmail,
    setEditEmail,
}: DashboardPersonalEmailAddressEditProps) {
    const { handleError, errorMessage, clearError } = useHandleError();
    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState({ email: '' });

    const normalizeAndStripSpaces = (value: string) => {
        return value.normalize('NFKC').replace(/[\s\u3000]/g, '');
    };

    const handleAuthIdentifierBlur: React.FocusEventHandler<HTMLDivElement> = (e) => {
        const target = e.target as Element;
        if (!(target instanceof HTMLInputElement)) return;
        const value = normalizeAndStripSpaces(target.value);
        handleEmailInputChange(value);
    };

    const handleEmailInputChange = (value: string) => {
        setFormData({
            ...formData,
            email: value,
        });
    };

    const handleSubmit = async () => {
        try {
            clearError();
            setIsProcessing(true);
            const response = await apiFetch(`/users/me/email/update/activation/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            goToAuth();
            setEditEmail(response.email);
        } catch (err: unknown) {
            handleError(err);
            setIsProcessing(false);
        }
    };

    return (
        <>
            <div className="flex flex-col w-full">
                <div className="section-spacer-inner-y"></div>
                <div className="flex justify-start items-center w-full py-4">
                    <FormSectionTitle title="メールアドレス設定" />
                </div>
                <div className="flex w-full pt-2">
                    <DashboardPersonalNoteUnit
                        messages={[
                            'ログインやパスワードの再設定、お知らせの受け取りに使うメールアドレスです。',
                            // 'スクーミーでは、卒業や異動などで所属先が変わった後も継続してご利用いただけるよう、こちらには個人のメールアドレスのご登録をおすすめしています。',
                        ]}
                    />
                </div>
                <div className="flex flex-col items-start justify-start w-full py-4">
                    <div className="flex items-start justify-start w-full">
                        <ErrorBox message={errorMessage?.message} />
                    </div>
                    <div className="flex flex-col items-center w-full font-notojp">
                        <div className="flex justify-start items-center w-full">
                            <FormFieldLabel title="現在のメールアドレス" />
                        </div>
                        <div className="my-2"></div>
                        <div className="flex w-full px-4 text-sm md:text-base break-words break-all">
                            {currentEmail || 'メールアドレスは設定されていません'}
                        </div>
                    </div>
                    <div className="py-6 md:py-4"></div>
                    <div id="email" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full relative">
                            <FormFieldLabel title="新しいメールアドレス" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="email" />
                        </div>
                        <div className="flex w-full" onBlur={handleAuthIdentifierBlur}>
                            <FormInputWithLabel
                                id="email"
                                name="email"
                                label="254文字以内"
                                type="email"
                                maxLength={254}
                                autoComplete="off"
                                value={formData.email}
                                inputMode="email"
                                onChange={(e) => {
                                    handleEmailInputChange(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col items-start justify-start w-full py-2">
                <div className="flex flex-col-reverse md:flex-row justify-center w-full pt-12">
                    <div className="flex-grow"></div>
                    <div className="flex flex-col justify-end items-center">
                        <div className="flex justify-center items-center">
                            <ButtonGAdaptive
                                type="submit"
                                label="認証メールを送信する"
                                onClick={handleSubmit}
                                description="send verification email"
                                icon={Mail}
                                isProcessing={isProcessing}
                            />
                        </div>
                    </div>
                    <div className="flex-grow"></div>
                </div>
            </div>
        </>
    );
}

export default function DashboardPersonalEmailAddress() {
    const [viewState, setViewState] = useState<'edit' | 'auth'>('edit');
    const [editEmail, setEditEmail] = useState<string | null>(null);
    const [currentEmail, setCurrentEmail] = useState<string | null>(null);
    const { handleError } = useHandleError();

    useEffect(() => {
        (async () => {
            try {
                const data = await apiFetch(`/users/me/email`);
                setCurrentEmail(data.email || null);
            } catch (err: unknown) {
                handleError(err);
            }
        })();
    }, []);

    return (
        <div className="flex flex-col w-full md:w-[98%] section-4-b bg-color-white-base">
            <div className="flex w-full items-center">
                <div className="flex flex-grow justify-start items-center w-full">
                    <SectionTitle title={`Email Address`} />
                </div>
            </div>

            {viewState === 'edit' && (
                <DashboardPersonalEmailAddressEdit
                    goToAuth={() => setViewState('auth')}
                    currentEmail={currentEmail}
                    setEditEmail={setEditEmail}
                />
            )}
            {viewState === 'auth' && <DashboardPersonalEmailAddressAuth editEmail={editEmail} />}
        </div>
    );
}
